import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { Activity, Building2, CalendarDays, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useSuperadminAnalytics } from '@/hooks/useSuperadmin';
import { BarList, DailyChart, TypesDonut } from '@/components/superadmin/analytics/charts';
import { Kpi, PageFrame, RangeToggle } from '@/components/superadmin/analytics/shared';
import { nf, useDays } from '@/components/superadmin/analytics/utils';

/** Level 1: the whole platform. Organisations and screens drill down. */
const SuperadminAnalytics = () => {
  const navigate = useNavigate();
  const [days, setDays, q] = useDays();
  const { data, loading, error } = useSuperadminAnalytics(days);
  const t = data?.totals;
  const maxTenant = Math.max(1, ...(data?.top_tenants ?? []).map((x) => x.actions));

  return (
    <PageFrame title="Platform Analytics" subtitle="Usage across all organisations. Select an organisation or a screen to drill in." right={<RangeToggle days={days} onChange={setDays} />}>
      {error && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">Could not load analytics: {error}</p>}
      {loading && !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : data && t ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Active organisations" value={t.active_tenants} hint={`of ${nf(t.tenants)} in the last ${days} days`} icon={Building2} />
            <Kpi label="Active users" value={t.active_users} hint={`DAU ${nf(t.dau)} · WAU ${nf(t.wau)} · MAU ${nf(t.mau)}`} icon={Users} />
            <Kpi label="Tracked actions" value={t.actions} hint={`last ${days} days`} icon={Activity} />
            <Kpi label="Avg actions / active user" value={t.active_users ? (t.actions / t.active_users).toFixed(1) : '0'} hint="engagement depth" icon={CalendarDays} />
          </div>

          {t.actions === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">No activity recorded in this period.</p>
          ) : (
            <>
              <Card>
                <CardHeader><CardTitle className="text-base">Daily activity</CardTitle><CardDescription>Actions (left axis) and unique users (right axis), all organisations</CardDescription></CardHeader>
                <CardContent><DailyChart data={data.daily} /></CardContent>
              </Card>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader><CardTitle className="text-base">Most used screens</CardTitle><CardDescription>Select a screen to see which organisations use it</CardDescription></CardHeader>
                  <CardContent>
                    {data.top_views.length === 0 ? <p className="text-sm text-muted-foreground">No screen usage recorded.</p>
                      : <BarList data={data.top_views} onSelect={(v) => navigate(`/superadmin/analytics/view/${encodeURIComponent(v)}${q}`)} height={260} />}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Action types</CardTitle><CardDescription>What people do, by event type</CardDescription></CardHeader>
                  <CardContent><TypesDonut data={data.action_types} /></CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader><CardTitle className="text-base">Most active organisations</CardTitle><CardDescription>Top 10 by tracked actions in the last {days} days. Select one to drill in.</CardDescription></CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th scope="col" className="py-2 text-start font-normal">Organisation</th>
                        <th scope="col" className="py-2 text-start font-normal">Plan</th>
                        <th scope="col" className="py-2 text-end font-normal">Users</th>
                        <th scope="col" className="py-2 ps-6 text-start font-normal">Actions</th>
                        <th scope="col" className="py-2 text-end font-normal">Last active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_tenants.map((o) => (
                        <tr key={o.tenant_id} className="border-b border-border last:border-0 hover:bg-secondary/50">
                          <td className="py-2.5"><Link to={`/superadmin/analytics/org/${o.tenant_id}${q}`} className="font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">{o.name}</Link> <span className="font-mono text-xs text-muted-foreground">{o.slug}</span></td>
                          <td className="py-2.5 capitalize">{o.plan}</td>
                          <td className="py-2.5 text-end font-mono">{nf(o.users)}</td>
                          <td className="py-2.5 ps-6"><span className="flex items-center gap-2"><span className="block h-1.5 w-32 overflow-hidden rounded-full bg-muted" aria-hidden="true"><span className="block h-full rounded-full bg-primary" style={{ width: `${(o.actions / maxTenant) * 100}%` }} /></span><span className="font-mono text-xs">{nf(o.actions)}</span></span></td>
                          <td className="py-2.5 text-end text-xs text-muted-foreground">{formatDistanceToNowStrict(new Date(o.last_active))} ago</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      ) : null}
    </PageFrame>
  );
};

export default SuperadminAnalytics;
